import { useState } from 'react';
import { useShopTier } from '../../hooks/useShopTier';
import { startBillingCheckout } from '../../services/billing-service';
import { PILOT_ENDS, FOUNDERS_PRICE, FREE_FOREVER_LINE, ALWAYS_FREE } from '../../config/pricing-terms';

interface Props {
  shopId: string | null;
}

/**
 * Upgrade / plan-status card. Renders on the SETUP hub landing (mockup #8b)
 * and on the ACCOUNT settings sub-page — coral white-card idiom (Task 15);
 * still uses alert() on checkout failure.
 *
 * States (in priority order) — none but the last render a buy button, so a
 * shop mid-checkout or already canceled-but-unlapsed can't double-subscribe:
 *  1. billingStatus === 'active'                        -> plan line + cancel-by-email affordance.
 *  2. billingStatus === 'past_due'                       -> red warning, nothing to click.
 *  3. billingStatus === 'pending'                        -> payment processing, nothing to click.
 *  4. billingStatus === 'canceled' && paidUntil in future -> access-until date, nothing to click.
 *  5. otherwise (upgradeable, incl. canceled-and-lapsed)  -> founders price (if founding identity)
 *                                                            and/or standard pro pricing.
 */
export default function BillingCard({ shopId }: Props) {
  const { data: tierInfo } = useShopTier(shopId);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);

  if (!tierInfo) return null;

  const { tier, billingStatus, paidUntil, foundingNumber } = tierInfo;

  async function handleUpgrade(plan: string) {
    setBusyPlan(plan);
    try {
      await startBillingCheckout(plan); // redirects the browser to Square on success
    } catch (e) {
      setBusyPlan(null);
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : 'Checkout failed');
    }
  }

  // Founders identity is durable — a lapsed founding shop reads effective
  // tier 'free' but keeps foundingNumber, so it still qualifies for the
  // founders-monthly reactivation price.
  const hasFoundingIdentity = tier === 'founding' || foundingNumber != null;

  return (
    <div className="bg-white rounded-[10px] p-[18px]">
      <h2 className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Billing</h2>

      {billingStatus === 'active' ? (
        <div>
          <p className="font-heading text-[8px] text-[#2f9440] mb-3.5">
            PRO ACTIVE · RENEWS {paidUntil ? paidUntil.slice(0, 10) : 'SOON'}
          </p>
          <p className="font-grotesk font-medium text-[12px] leading-[1.5] text-body-grey">
            To cancel, email{' '}
            <a href="mailto:hello@bit-brew.coffee?subject=Cancel subscription" className="text-coral underline">
              hello@bit-brew.coffee
            </a>{' '}
            — Pro stays on until the end of the paid period.
          </p>
        </div>
      ) : billingStatus === 'past_due' ? (
        <p className="font-grotesk font-medium text-sm text-error">
          Payment issue — check your card (Square email has the fix link).
        </p>
      ) : billingStatus === 'pending' ? (
        <p className="font-grotesk font-medium text-sm text-teal-ink">
          Payment processing — Pro activates shortly. No need to pay again.
        </p>
      ) : billingStatus === 'canceled' && paidUntil && new Date(paidUntil) > new Date() ? (
        <p className="font-grotesk font-medium text-sm text-body-grey">
          Plan canceled — Pro access until {paidUntil.slice(0, 10)}. Resubscribe any time after.
        </p>
      ) : (
        <div>
          {/* The terms, stated before any price. A café must never learn what
              is chargeable from an invoice — see config/pricing-terms.ts. */}
          {hasFoundingIdentity && (
            <div className="pixel-corners-sm bg-background border-2 border-hairline p-3 mb-3.5">
              <p className="font-heading text-[8px] text-coral mb-1.5 uppercase">
                Founding member{foundingNumber != null ? ` #${foundingNumber}` : ''}
              </p>
              <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-primary">
                Everything is free until <span className="font-bold">{PILOT_ENDS}</span>, analytics
                included. After that your price is <span className="font-bold">{FOUNDERS_PRICE}</span>,
                locked for life — it never rises, even when the standard price does.
              </p>
            </div>
          )}
          <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-primary mb-2">
            {FREE_FOREVER_LINE}
          </p>
          <p className="font-grotesk font-medium text-[12px] leading-[1.5] text-body-grey mb-3.5">
            {ALWAYS_FREE} Paying adds analytics: regulars, at-risk customers, peak hours, and
            free-coffee comps.
          </p>
          <div className="flex flex-col gap-2.5">
            {hasFoundingIdentity && (
              <button
                onClick={() => handleUpgrade('founders-monthly')}
                disabled={busyPlan !== null}
                className="rounded-full bg-accent-alt border-2 border-primary text-primary font-grotesk font-bold text-[13px] py-3 px-4 disabled:opacity-50"
              >
                {busyPlan === 'founders-monthly' ? 'Upgrading…' : 'A$29/mo — founders price, locked for life'}
              </button>
            )}
            {!(tier === 'founding') && (
              <div className="flex gap-2.5">
                <button
                  onClick={() => handleUpgrade('pro-monthly')}
                  disabled={busyPlan !== null}
                  className="flex-1 rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 disabled:opacity-50 hover:border-coral hover:text-coral"
                >
                  {busyPlan === 'pro-monthly' ? 'Upgrading…' : 'A$69/mo'}
                </button>
                <button
                  onClick={() => handleUpgrade('pro-annual')}
                  disabled={busyPlan !== null}
                  className="flex-1 rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 disabled:opacity-50 hover:border-coral hover:text-coral"
                >
                  {busyPlan === 'pro-annual' ? 'Upgrading…' : 'A$690/yr (2 months free)'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
